import React, { useState, useRef, useEffect } from "react";
import { Row, Col } from "reactstrap";
import AmazonProductListingEditableField from "./AmazonProductListingEditableField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function normalizeImages(val) {
  if (!val) return [];

  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (typeof item === "string") return item;

        if (typeof item === "object") {
          return (
            item.media_location ||
            item.link ||
            item.url ||
            null
          );
        }

        return null;
      })
      .filter(Boolean);
  }

  return [];
}

function AmazonProductListingFieldRow(props) {
  const catalogRef = useRef(null);
  const listingRef = useRef(null);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [isListingExpanded, setIsListingExpanded] = useState(false);
  const [isCatalogOverflow, setIsCatalogOverflow] = useState(false);
  const [isListingOverflow, setIsListingOverflow] = useState(false);

  const catalogVal = get(props.field, "catalog.value", null);
  const listingVal = get(props.field, "listing.value", null);

  useEffect(() => {
    const checkOverflow = () => {
      if (catalogRef.current) {
        setIsCatalogOverflow(catalogRef.current.scrollHeight > catalogRef.current.clientHeight);
      }
      if (listingRef.current) {
        setIsListingOverflow(listingRef.current.scrollHeight > listingRef.current.clientHeight);
      }
    };

    setTimeout(checkOverflow, 0);

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [catalogVal, listingVal]);

  if (!props.field) return null;

  function renderValue(val) {
    if (val === null || val === undefined) return "—";
  
    // Recursively map over arrays
    if (Array.isArray(val)) {
      if (val.length === 0) return "—";
      return val.map((item, i) => (
        <div key={i} className="field-value-object-group" style={{ marginBottom: '8px',borderBottom: '1px dashed #eee' }}>
          {renderValue(item)}
        </div>
      ));
    }
  
    // Recursively map over objects ensuring deep nesting is handled
    if (typeof val === "object") {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '4px' }}>
          {Object.entries(val).map(([k, v]) => (
            <div key={k} className="field-value-object" style={{ fontSize: '11px' }}>
              <b style={{ color: '#555', marginRight: '4px' }}>{k}:</b> 
              {typeof v === 'object' && v !== null ? (
                <div style={{ paddingLeft: '8px', borderLeft: '1px solid #ccc', margin: '2px 0' }}>
                  {renderValue(v)}
                </div>
              ) : (
                <span>{String(v)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }
  
    return String(val);
  }

  const renderClampedText = (content, ref, isExpanded, setExpanded, isOverflowing) => (
    <div className="clamped-text-wrapper">
      <div
        ref={ref}
        className={`clamped-text-content ${isExpanded ? "clamped-text-expanded" : "clamped-text-collapsed"}`}
      >
        {content}
      </div>
      {isOverflowing && !isExpanded && (
        <span className="read-more-btn" onClick={() => setExpanded(true)}>
          ... Read more
        </span>
      )}
      {isExpanded && (
        <span className="read-less-btn" onClick={() => setExpanded(false)}>
          Read less
        </span>
      )}
    </div>
  );
  
  var isMandatory = get(props.field, "schema.is_mandatory", false);
  var status = get(props.field, "validation.status", "VALID");
  var label = props.field.label || props.field.field || "";
  var description = get(props.field, "schema.description", "");

  var statusClass = status ? status.toLowerCase() : "valid";
  
  const isImageField =
    props.field.field === "images" ||
    (props.field.field && props.field.field.includes("image_locator")) ||
    (props.field.ui && props.field.ui.input_type === "image");

    const isUpdated = props.changedFields && props.changedFields.hasOwnProperty(props.field.field);
    const updatedValue = isUpdated ? props.changedFields[props.field.field].value : undefined;


  return (
    <Row className="g-2 px-3 py-2 border-bottom align-items-center m-0 field-row-container">
      <Col md={3} className="text-truncate field-row-label">
        <p className={isImageField ? "mb-0 cursor-pointer" : 'mb-0'} onClick={() => isImageField ? props.action(props.field) : undefined}> 
          {label} 
          {isMandatory && <span className="field-row-mandatory text-danger ms-1">*</span>}
          
          {description && (
            <span title={description} style={{ cursor: "pointer", marginLeft: "8px", color: "#6c757d" }}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          )}
        </p>
      </Col>
      
      <Col md={2}>
        {isImageField ? (
          <div className="field-image-container">
            {normalizeImages(catalogVal).map((img, i) => (
              <img key={i} src={img} alt="img" className="field-image-item" />
            ))}
          </div>
        ) : (
          renderClampedText(renderValue(catalogVal), catalogRef, isCatalogExpanded, setIsCatalogExpanded, isCatalogOverflow)
        )}
      </Col>

      <Col md={2}>
        {isImageField ? (
          <div className="field-image-container">
            {normalizeImages(listingVal).map((img, i) => (
              <img key={i} src={img} alt="img" className="field-image-item" />
            ))}
          </div>
        ) : (
          renderClampedText(renderValue(listingVal), listingRef, isListingExpanded, setIsListingExpanded, isListingOverflow)
        )}
      </Col>
      
      <Col md={3}>
        <AmazonProductListingEditableField field={props.field} onFieldChange={props.onFieldChange}  
        changedValue={updatedValue}
        isUpdated={isUpdated}
        />
      </Col>
      
      <Col md={2}>
        <span className={`status-badge status-badge-${statusClass}`}>
          {status}
        </span>
      </Col>
    </Row>
  );
}

export default AmazonProductListingFieldRow;